<template>
  <div class="max-width">
    <v-row>
      <v-col cols="12" align="center" justify="center">
        <v-row>
          <v-col cols="12">
            <h2 align="center" justify="center">1-on-1 Onboarding</h2>
          </v-col>
        </v-row>
        <v-data-table
          height="600"
          :headers="table.headers"
          :options="table.options"
          :items="subscriptions"
          :item-class="rowClass"
          :search="search"
          :show-group-by="true"
          :hide-default-footer="true"
          disable-pagination
          fixed-header
          dense
          :loading="$apollo.loading"
        >
          <template v-slot:top>
            <v-text-field v-model="search" class="mx-4 mt-0 pt-0">
              <template v-slot:label>
                <v-icon color="secondary"> mdi-magnify </v-icon>
                Search
              </template>
            </v-text-field>
          </template>

          <template v-slot:[`item.profile_pic`]="props">
            <profile-pic :profilePic="props.item.profile_pic" :userId="props.item.userId" />
          </template>

          <template v-slot:[`header.client_name`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.client_name`]="props">
            <client-name
              :client-id="props.item.userId"
              :dob="props.item.dateOfBirth"
              :profileLink="props.item.profile_link"
              :subscriptionEndDate="props.item.subscriptionEndDate"
              :clientName="props.item.client_name"
              :username="props.item.username"
              :contactLink="props.item.contact_link"
              :lastActive="props.item.client.userProfile.lastActive"
              :nextSubscription="props.item.nextSubscription"
              :coachingChat="props.item.client.userProfile.coachingChat"
              :supportChat="props.item.client.userProfile.supportChat"
              :coachId="coachId"
              :subscriptionSurveyId="props.item.subscriptionSurveyId"
              :bhag="props.item.has_goal"
              :checkinGoal="props.item.has_checkin_goal"
              :weeksOnProgram="props.item.weeksOnProgram"
            />
          </template>

          <template v-slot:[`header.product`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`header.comms`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.comms`]="props">
            <comms
              :item="props.item"
              @update-am="updateCheckbox(props.item)"
              @update-pm="updateCheckbox(props.item, (day_half = 'Afternoon'))"
            />
          </template>

          <template v-slot:[`item.controls`]="props" width="100">
            <tool-buttons
              :coachId="coachId"
              :userId="props.item.userId"
              :username="props.item.username"
              :clientName="props.item.client_name"
              :activityLoggedToday="props.item.activity_logged_today"
              :injectedData="props.item.user"
              class="text-center mb-2 mt-1"
            />
            <client-week-dots :prizeData="props.item.prizeEligibility" show-arrows :userId="props.item.userId" class="mb-1" />
          </template>

          <template v-slot:[`header.activeWorkoutBlocks`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.activeWorkoutBlocks`]="props">
            <active-workout-blocks
              :workoutBlocks="props.item.activeWorkoutBlocks"
              :nextWorkoutBlocks="props.item.nextWorkoutBlocks"
            />
          </template>

          <template v-slot:[`header.nutritionPlan`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.nutritionPlan`]="props">
            <span class="block caption"> <b>Plan:</b> {{ props.item.nutritionPlan.title }} </span>
            <span class="block caption"> <b>Block:</b> {{ props.item.nutritionBlock }} </span>
          </template>

          <template v-slot:[`header.lastMessage`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.lastMessage`]="props">
            <last-message-column @viewed-message="onMessageViewed" :message="props.item.lastMessage" />
          </template>
          <template v-slot:[`header.resolved`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.resolved`]="props">
            <resolve-message-btn :chat="props.item.chat" :resolved="props.item.resolved" @reload="reloadData" />
          </template>

          <template v-slot:[`header.medicalAssessment`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>

          <template v-slot:[`header.assessment`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.assessment`]="props">
            <span v-if="props.item.assessment == null">
              No booking
            </span>
            <span v-else-if="props.item.assessment.assessmentDate">
              Assessment uploaded
            </span>
            <span v-else-if="props.item.assessment.requiredDate">
              Assessment pending uploaded
            </span>
          </template>

        </v-data-table>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { COACHING_ONBOARDING_SUBSCRIPTION_QUERY } from "@/constants/graphql/coach/coaching";
import { getUserName } from "@/lib/username";
import CoachingDashboardBase from "../mixins/CoachingDashboardBase";
import MetricMixin from "@/components/shared/mixins/MetricMixin";

export default {
  name: "CoachingOneOnOne",
  props: ["coachId"],
  mixins: [CoachingDashboardBase, MetricMixin],
  data() {
    return {
      skipDashboardListingQuery: true,
      search: "",
      selectedUsername: "",
      selectedFullname: "",
      subscriptions: [],
      selectedUserId: null,
      table: {
        options: {
          showGroupBy: true,
          multiSort: false,
        },
        headers: [
          { text: "", value: "profile_pic", groupable: false },
          { text: "Client", value: "client_name", groupable: false },
          { text: "Product", value: "product", groupable: false },
          {
            text: "Daily comms",
            value: "comms",
            align: "center",
            sortable: true,
            groupable: false,
            help: "Client communications AM / PM ",
          },
          {
            text: "",
            value: "controls",
            sortable: false,
            groupable: false,
            width: 120,
          },

          { text: "Blocks", value: "activeWorkoutBlocks", groupable: false, help: "Current workout blocks" },
          { text: "Nutrition", value: "nutritionPlan", groupable: false },
          { text: "Last Message", value: "lastMessage", groupable: false },
          { text: "", value: "resolved", groupable: false, help: "Resolve any outstanding messages from this client" },
          {
            text: "Medical assessment done",
            value: "medicalAssessment",
            groupable: false,
          },
          {
            text: "Check-in status",
            value: "assessment",
            groupable: false,
          },
        ],
      },
    };
  },
  methods: {
    openCalendar(userId) {
      this.selectedUserId = userId;
      this.modals.calendar = true;
    },
    closeCalendar() {
      this.selectedUserId = null;
      this.modals.calendar = false;
    },
  },
  apollo: {
    userSubscriptions: {
      query: COACHING_ONBOARDING_SUBSCRIPTION_QUERY,
      variables() {
        let encoded_json_payload = btoa("UserNode:" + this.coachId);

        let input = {
          stage: "onboarding",
          productType: "one-on-one",
          coachId: encoded_json_payload,
          selectedDate: this.$moment().format("YYYY-MM-DD"),
        };
        return input;
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data && !response.loading) {
          this.subscriptions = response.data.userSubscriptions.edges.map((item) => {
            var assessments = item.node.coachingAssessments.filter((assessment) => {
              return this.$moment(assessment.createdAt) >= this.$moment(item.node.startDate);
            });

            var activeBlocks = item.node.user.subscriptionblockSet.filter((block) => {
              return this.$moment(block.startDate) <= this.$moment() && this.$moment(block.endDate) >= this.$moment();
            });

            var nextBlocks = item.node.user.subscriptionblockSet.filter((block) => {
              return this.$moment(block.startDate) > this.$moment() && this.$moment(block.endDate) >= this.$moment();
            });

            return {
              id: item.node.id,
              userId: item.node.user.id,
              profile_pic: item.node.user.userProfile.profilePicture,
              profile_link: `/client-profile/${this.coachId}/${item.node.user.id}`,
              contact_link: `/clients/${item.node.user.id}`,
              client_name: getUserName(item.node.user),
              username: item.node.user.username,
              product: item.node.product.name,
              client: item.node.user,
              dateOfBirth: item.node.user.userProfile.dateOfBirth,
              signUpDate: this.$moment(item.node.startDate).format("YYYY-MM-DD"),
              programStartDate: item.node.programStartDate,
              assessment: assessments.length > 0 ? assessments[0] : null,
              medicalAssessment: item.node.submittedMedicalForm ? "Yes" : "No",
              comms: {
                completed_am:
                  item.node.coachactivitySet.edges.filter((val) => {
                    return val.node.dayHalf == "MORNING";
                  }).length > 0,
                completed_pm:
                  item.node.coachactivitySet.edges.filter((val) => {
                    return val.node.dayHalf == "AFTERNOON";
                  }).length > 0,
              },
              nutritionPlan: item.node.user.userProfile?.nutritionPlan?.name ?? "not set",
              nutritionBlock:
                item.node.user.subscriptionnutritionblockSet.edges.length > 0
                  ? item.node.user.subscriptionnutritionblockSet.edges[0].node.nutritionBlock.title
                  : "No block selected",
              activeWorkoutBlocks: activeBlocks ? activeBlocks : [],
              nextWorkoutBlocks: nextBlocks ? nextBlocks : [],
              has_goal: item.node.hasBhag,
              has_checkin_goal: item.node.hasCheckinGoal,
              chat: item.node.user.userProfile.coachingChat,
              lastMessage: item.node.user.userProfile.coachingChat?.lastMessage,
              resolved: item.node.user.userProfile.coachingChat?.resolved ?? false,
            };
          });
        }
        if (response.error && response.error.gqlError?.message == "Permission Denied.") {
          this.$router.push("/no-access");
        }
      },
    },
  },
};
</script>

<style scoped>
.max-width {
  width: 100%;
}

.exercise-dot {
  position: absolute;
  top: 16px;
  background-color: var(--v-exercise-base);
  border: 1px solid white;
  border-radius: 100%;
  width: 22px;
  height: 22px;
}
.block {
  display: block;
}
</style>
